import { Link } from "react-router-dom";
import { useArticles } from "../../../hooks/useArticles";
import { toNewsItems } from "../../../lib/articleAdapter";
import { matchesCategory } from "../../../lib/categoryAliases";

const sectionConfig = [
	{
		id: "Internacionales",
		icon: "ri-earth-line",
		color: "bg-blue-500",
		accent: "text-blue-600",
		lightBg: "bg-blue-50",
		border: "border-blue-100",
	},
	{
		id: "Educación",
		icon: "ri-graduation-cap-line",
		color: "bg-amber-500",
		accent: "text-amber-600",
		lightBg: "bg-amber-50",
		border: "border-amber-100",
	},
	{
		id: "Estadísticas",
		icon: "ri-bar-chart-line",
		color: "bg-emerald-500",
		accent: "text-emerald-600",
		lightBg: "bg-emerald-50",
		border: "border-emerald-100",
	},
];

export default function ThematicSections() {
	const { articles, loading } = useArticles();
	const allItems = toNewsItems(articles);

	const sections = sectionConfig.map((cfg) => {
		const real = allItems.filter((a) =>
			matchesCategory(
				a.category || "",
				Array.isArray(a.tags) ? a.tags : [],
				cfg.id,
			),
		);
		return {
			...cfg,
			articles: real.slice(0, 3),
			hasRealContent: real.length > 0,
		};
	});

	if (loading) {
		return (
			<section className="bg-white py-6 md:py-12">
				<div className="max-w-screen-xl mx-auto px-4 md:px-6">
					<div className="h-8 bg-slate-200 rounded w-1/3 mx-auto mb-6 md:mb-12 animate-pulse" />
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8">
						{[1, 2, 3].map((i) => (
							<div key={i} className="space-y-4">
								<div className="h-10 bg-slate-100 rounded-xl w-1/2 animate-pulse" />
								<div className="h-20 bg-slate-100 rounded-xl animate-pulse" />
								<div className="h-20 bg-slate-100 rounded-xl animate-pulse" />
							</div>
						))}
					</div>
				</div>
			</section>
		);
	}

	return (
		<section className="bg-white py-6 md:py-12">
			<div className="max-w-screen-xl mx-auto px-4 md:px-6">
				<div className="text-center mb-6 md:mb-12">
					<div className="flex items-center justify-center gap-3 mb-3">
						<div className="h-px w-12 bg-slate-200" />
						<div className="h-px w-12 bg-slate-200" />
					</div>
					<h2
						className="text-[#0B1F3A] font-extrabold"
						style={{
							fontFamily: "'Sora', sans-serif",
							fontSize: "clamp(1.5rem, 4vw, 2.2rem)",
						}}
					>
						Foco Aéreo
					</h2>
				</div>

				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
					{sections.map((section) => (
						<div
							key={section.id}
							className={`rounded-2xl border ${section.border} bg-slate-50 overflow-hidden`}
						>
							<div
								className={`${section.lightBg} px-5 py-4 flex items-center justify-between border-b ${section.border}`}
							>
								<div className="flex items-center gap-3">
									<div
										className={`w-9 h-9 flex items-center justify-center rounded-xl ${section.color}`}
									>
										<i className={`${section.icon} text-white text-base`} />
									</div>
									<h3
										className="text-[#0B1F3A] font-bold text-sm"
										style={{ fontFamily: "'Sora', sans-serif" }}
									>
										{section.id}
									</h3>
								</div>
								<Link
									to={`/actualidad?categoria=${encodeURIComponent(section.id)}`}
									className={`text-[10px] font-bold uppercase tracking-widest ${section.accent} hover:opacity-70 transition-opacity cursor-pointer whitespace-nowrap flex items-center gap-1`}
									style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
								>
									Ver más <i className="ri-arrow-right-line" />
								</Link>
							</div>
							<div className="divide-y divide-slate-200/60">
								{section.hasRealContent ? (
									section.articles.map((article, idx) => (
										<Link
											key={article.id}
											to={`/articulo/${article.slug}`}
											className="flex gap-3 p-4 hover:bg-white transition-colors group/item cursor-pointer"
										>
											<div
												className="flex-shrink-0 rounded-lg overflow-hidden bg-slate-200"
												style={{ width: "72px", height: "56px" }}
											>
												<img
													src={article.image}
													alt={article.title}
													className="w-full h-full object-cover object-center group-hover/item:scale-105 transition-transform duration-500"
													onError={(e) => {
														(e.target as HTMLImageElement).src =
															"https://readdy.ai/api/search-image?query=aviation%20aircraft%20Caribbean%20blue%20sky%20professional%20photography&width=200&height=150&seq=fallback-thematic&orientation=landscape";
													}}
												/>
											</div>
											<div className="flex-1 min-w-0">
												{idx === 0 && (
													<span
														className={`inline-block text-[9px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full mb-1 ${section.lightBg} ${section.accent}`}
														style={{
															fontFamily: "'Plus Jakarta Sans', sans-serif",
														}}
													>
														Destacado
													</span>
												)}
												{article.isInternational && (
													<span className="inline-flex items-center gap-1 text-blue-600 text-[9px] font-bold tracking-widest uppercase px-1.5 py-0.5 rounded-full bg-blue-50 border border-blue-200 ml-1">
														<i className="ri-earth-line text-[10px]" />
														Internacional
													</span>
												)}
												<p
													className="text-[#0B1F3A] font-semibold text-xs leading-snug line-clamp-2 group-hover/item:text-slate-600 transition-colors"
													style={{ fontFamily: "'Sora', sans-serif" }}
												>
													{article.title}
												</p>
												<span
													className="text-slate-400 text-[10px]"
													style={{
														fontFamily: "'Plus Jakarta Sans', sans-serif",
													}}
												>
													{article.date}
												</span>
											</div>
										</Link>
									))
								) : (
									<div className="px-4 py-5 text-center">
										<i className={`${section.icon} text-slate-300 text-lg mb-1.5 block`} />
										<p className="text-slate-400 text-[11px]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
											Próximamente
										</p>
									</div>
								)}
							</div>
						</div>
					))}
				</div>

				<div className="mt-6 md:mt-10 text-center">
					<Link
						to="/actualidad"
						className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#0B1F3A] hover:bg-[#0d2547] text-white text-sm font-bold rounded-xl transition-all cursor-pointer whitespace-nowrap"
						style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
					>
						<i className="ri-newspaper-line" />
						Ver todo el contenido
						<i className="ri-arrow-right-line" />
					</Link>
				</div>
			</div>
		</section>
	);
}